import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MyArvatoEnvironment } from '@myarvato/shared/models';
import { DataTableSearchDto } from '@myarvato/shared/table';
import { TableLazyLoadEvent } from 'primeng/table';
import { Observable, of } from 'rxjs';
import { DetailViewDto, OverviewDto } from '@myarvato/modules/transport/models';

@Injectable({
  providedIn: 'root',
})

/**
 * Service to handle overview-related operations.
 */
export class TransportDataService {
  // injections
  private readonly environment = inject(MyArvatoEnvironment);
  private readonly httpClient = inject(HttpClient);

  // fields
  private readonly baseUrl = `${this.environment.url.api}Transport/`;

  /**
   * Retrieves the overview data based on the provided filter.
   * @param filter - The filter criteria for loading the overview data.
   * @returns An Observable of OverviewDto containing the overview data.
   */
  public getOverview(filter: TableLazyLoadEvent): Observable<OverviewDto> {
    if (filter.last === 0) {
      return of({ count: 0, records: [] });
    }

    return this.httpClient.post<OverviewDto>(`${this.baseUrl}`, filter, this.getRequestOptions());
  }

  public getTreeOverview(filterDto: DataTableSearchDto): Observable<OverviewDto> {
    // Wenn first + rows = 0, schicke keine echten Daten
    if ((filterDto.first ?? 0) + (filterDto.rows ?? 0) === 0) {
      return of({ count: 0, records: [] });
    }
    return this.httpClient.post<OverviewDto>(`${this.baseUrl}`, filterDto, this.getRequestOptions());
  }

  public getDetailsByTrackingAndDeliveryNumber(
    trackingNumber: string | undefined,
    arvatoDeliveryNumber: string | undefined,
  ): Observable<DetailViewDto | undefined> {
    if (!trackingNumber || !arvatoDeliveryNumber) {
      return of(undefined);
    }

    return this.httpClient.post<DetailViewDto>(
      `${this.baseUrl}Details/ByTrackingAndDeliveryNumber`,
      { trackingNumber, arvatoDeliveryNumber },
      this.getRequestOptions(),
    );
  }

  public getDetailsById(id: string | undefined): Observable<DetailViewDto | undefined> {
    if (!id) {
      return of(undefined);
    }

    return this.httpClient.post<DetailViewDto>(`${this.baseUrl}Details/ById`, { id }, this.getRequestOptions());
  }

  private getRequestOptions() {
    const headers = new HttpHeaders({
      'x-skip-all-interceptor-handling': 'true',
      'x-error-message-target-key': 'none',
    });

    return { headers: headers };
  }
}
